/**
 * StratIQX Service Configuration
 * Central source of truth for all service definitions
 */

import { ServiceConfig } from './types/service.types';

export const SERVICES_CONFIG: Record<string, ServiceConfig> = {
  'market-essentials': {
    id: 'market-essentials',
    name: 'Market Essentials',
    description: 'Executive-grade market intelligence leveraging proprietary AI research methodology. Comprehensive strategic analysis typically valued at $25,000+, delivered through our next-generation technology platform.',
    price: {
      value: 1495,
      display: '$1,495'
    },
    deposit: {
      type: 'percentage',
      value: 20,
      amount: 299,
      display: '20% deposit required'
    },
    billingType: 'one-time',
    features: [
      'Executive-grade market analysis (15-20 pages)',
      'Proprietary AI-powered competitive intelligence',
      'Strategic opportunity assessment & validation',
      'C-suite ready executive presentation',
      'Strategic consultation call included',
      'Next day white-glove delivery'
    ],
    metadata: {
      deliveryTime: 'Next day',
      reportPages: '15-20',
      targetRevenue: '$1M-$5M'
    },
    icon: 'TrendingUp',
    popular: true,
    active: true,
    comingSoon: false,  // Popular active service
    displayOrder: 1
  },

  'market-intelligence': {
    id: 'market-intelligence',
    name: 'Market Intelligence Pro',
    description: 'Enterprise-grade strategic intelligence suite utilizing advanced AI orchestration and human expertise. Comprehensive market analysis typically valued at $75,000+ by traditional consulting firms.',
    price: {
      value: 2995,  // AI-amplified pricing
      display: '$2,995'
    },
    deposit: {
      type: 'percentage',
      value: 20,
      amount: 599,
      display: '20% deposit required'
    },
    billingType: 'one-time',
    features: [
      'Enterprise-grade market intelligence (25-35 pages)',
      'Advanced competitive landscape analysis',
      'Predictive market modeling & forecasting',
      'Strategic roadmap with implementation timeline',
      'Executive presentation & strategy session',
      'Priority analyst access',
      'Next day premium delivery'
    ],
    metadata: {
      deliveryTime: 'Next day',
      reportPages: '25-35',
      targetRevenue: '$5M-$20M'
    },
    icon: 'BarChart3',
    popular: false,
    active: false,
    comingSoon: true,  // Coming soon
    displayOrder: 2
  },
  
  'strategic-analysis': {
    id: 'strategic-analysis',
    name: 'Strategic Analysis & Advisory',
    description: 'Deep-dive strategic assessments with actionable insights for critical business decisions and market opportunities.',
    price: {
      value: 4995,  // AI-amplified pricing
      display: '$4,995'
    },
    deposit: {
      type: 'percentage',
      value: 20,
      amount: 999,
      display: '20% deposit required'
    },
    billingType: 'one-time',
    features: [
      'Industry-specific expertise',
      'Strategic framework development',
      'Risk assessment & mitigation',
      'Investment opportunity analysis',
      'Executive presentation ready'
    ],
    metadata: {
      deliveryTime: '3-4 weeks',
      includesPresentation: true
    },
    icon: 'Target',
    popular: false,
    active: false,
    comingSoon: false,  // Inactive
    displayOrder: 2
  },
  
  'ongoing-retainer': {
    id: 'ongoing-retainer',
    name: 'Ongoing Intelligence Retainer',
    description: 'Continuous market monitoring with monthly insights, alerts, and strategic updates to keep you ahead of market changes.',
    price: {
      value: 3500,  // Mid-point of 2000-5000
      display: '$3,500/month'
    },
    deposit: {
      type: 'fixed',
      value: 1000,
      amount: 1000,
      display: '$1,000 deposit required'
    },
    billingType: 'monthly',
    features: [
      'Real-time market monitoring',
      'Monthly insight reports',
      'Competitive intelligence alerts',
      'Quarterly strategic reviews',
      'Priority access to analyst'
    ],
    metadata: {
      deliveryTime: 'ongoing monthly',
      reportFrequency: 'monthly',
      analystAccess: true
    },
    icon: 'RotateCcw',
    popular: false,
    active: false,
    comingSoon: false,  // Inactive
    displayOrder: 3
  },

  'competitor-tracking': {
    id: 'competitor-tracking',
    name: 'Real-Time Competitor Intelligence',
    description: 'Advanced AI-powered competitor monitoring with instant alerts, pricing intelligence, and strategic move analysis for competitive SMBs.',
    price: {
      value: 3495,
      display: '$3,495/month'
    },
    deposit: {
      type: 'none',
      amount: 0,
      display: 'No setup fees'
    },
    billingType: 'monthly',
    features: [
      'Real-time competitor alerts',
      'Pricing intelligence dashboard',
      'Product launch tracking',
      'Strategic move analysis',
      'Weekly executive briefings'
    ],
    metadata: {
      deliveryTime: 'real-time',
      reportFrequency: 'continuous'
    },
    icon: 'Target',
    popular: false,
    active: false,
    comingSoon: false,  // Inactive
    displayOrder: 4
  },

  'market-pulse-executive': {
    id: 'market-pulse-executive',
    name: 'Market Pulse - Executive Report',
    description: 'Exclusive preview of our proprietary AI-orchestrated intelligence platform. Experience the same methodology used by Fortune 500 companies, delivered through cutting-edge technology.',
    price: {
      value: 0,
      display: 'Free Executive Report'
    },
    deposit: {
      type: 'none',
      amount: 0,
      display: 'No credit card required'
    },
    billingType: 'one-time',
    features: [
      'Premium market intelligence preview (5-10 pages)',
      'AI-orchestrated competitive analysis',
      'Strategic opportunity identification',
      'Executive-level ROI projections',
      'C-suite presentation format',
      'Interactive digital insights platform',
      'Qualification call with strategy consultant',
      'No commitment required'
    ],
    metadata: {
      deliveryTime: 'Next day',
      reportPages: '5-10 pages',
      includesPresentation: false,
      reportType: 'Executive Market Analysis'
    },
    icon: 'BarChart3',
    popular: false,
    active: true,
    displayOrder: 0  // Show first (free lead magnet)
  }
};

/**
 * Get all service IDs
 */
export const SERVICE_IDS = Object.keys(SERVICES_CONFIG) as const;

/**
 * Service ID type for type safety
 */
export type ServiceId = keyof typeof SERVICES_CONFIG;

/**
 * Export individual services for convenience
 */
export const MARKET_INTELLIGENCE = SERVICES_CONFIG['market-intelligence'];
export const STRATEGIC_ANALYSIS = SERVICES_CONFIG['strategic-analysis'];
export const ONGOING_RETAINER = SERVICES_CONFIG['ongoing-retainer'];